import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Aspect-ratio framed editorial photo. Prevents CLS and keeps the
 * black & white portraits consistently treated across the site.
 * Serves WebP with a JPEG fallback when `webp` is provided.
 */
export const PhotoFrame = ({
  src,
  webp,
  alt,
  ratio = '4 / 5',
  className,
  imgClassName,
  objectPosition = '50% 35%',
  caption,
  eager = false,
  zoom = true,
  rounded = 'rounded-[6px]',
  veil = false,
  testId,
}) => (
  <figure className={cn('m-0', className)} data-testid={testId}>
    <div
      className={cn(
        'rmh-photo w-full border border-[color:var(--rmh-border)]',
        zoom && 'rmh-photo--zoom',
        rounded,
      )}
      style={{ aspectRatio: ratio, boxShadow: 'var(--shadow-soft)' }}
    >
      <picture>
        {webp ? <source srcSet={webp} type="image/webp" /> : null}
        <img
          src={src}
          alt={alt}
          className={imgClassName}
          style={{ objectPosition }}
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : 'auto'}
          decoding="async"
        />
      </picture>
      {veil ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(180deg, rgba(20,19,18,0) 45%, rgba(20,19,18,0.28) 100%)',
          }}
        />
      ) : null}
    </div>
    {caption ? (
      <figcaption className="mt-3 flex items-center gap-2.5">
        <span
          className="block h-px w-6"
          style={{ backgroundColor: 'var(--rmh-accent)' }}
          aria-hidden="true"
        />
        <span className="rmh-eyebrow text-[0.6rem]">{caption}</span>
      </figcaption>
    ) : null}
  </figure>
);

export default PhotoFrame;
